import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { X, FileText, Copy, Download, Loader2, CheckCircle, FileDown } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import { escapeHtml } from '../utils/html'
import { formatDateTime } from '../utils/format'

interface SummaryModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  summary: string | null
  isLoading: boolean
  documentTitle?: string
}

export default function SummaryModal({ 
  isOpen, 
  onClose, 
  title, 
  summary, 
  isLoading, 
  documentTitle = 'Summary' 
}: SummaryModalProps) {
  const { t } = useTranslation()
  const [isCopied, setIsCopied] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  if (!isOpen) return null

  const handleCopyToClipboard = async () => {
    if (!summary) return
    
    try {
      await navigator.clipboard.writeText(summary)
      setIsCopied(true)
      setTimeout(() => setIsCopied(false), 2000)
    } catch (error) {
      console.error('Failed to copy to clipboard:', error)
    }
  }

  const handleDownloadText = () => {
    if (!summary) return
    
    const blob = new Blob([summary], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${documentTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_summary.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleDownloadPdf = async () => {
    if (!summary) return
    
    setIsGeneratingPdf(true)
    
    try {
      // Create a styled HTML content for PDF. The summary is LLM output and the
      // title may come from document metadata: every piece of text is
      // HTML-escaped before being interpolated into the markup.
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 40px;">
          <h1 style="color: #1a202c; border-bottom: 2px solid #e2e8f0; padding-bottom: 10px; margin-bottom: 30px;">
            ${escapeHtml(documentTitle)}
          </h1>
          <div style="color: #4a5568; font-size: 14px; margin-bottom: 20px;">
            Generated on: ${escapeHtml(formatDateTime(new Date()))}
          </div>
          <div style="line-height: 1.6; color: #2d3748;">
            ${summary.split('\n').map(line => {
              // Handle markdown-style headers
              if (line.startsWith('### ')) {
                return `<h3 style="color: #2b6cb0; margin-top: 20px; margin-bottom: 10px;">${escapeHtml(line.substring(4))}</h3>`
              } else if (line.startsWith('## ')) {
                return `<h2 style="color: #1a365d; margin-top: 25px; margin-bottom: 15px;">${escapeHtml(line.substring(3))}</h2>`
              } else if (line.startsWith('# ')) {
                return `<h1 style="color: #1a202c; margin-top: 30px; margin-bottom: 20px;">${escapeHtml(line.substring(2))}</h1>`
              } else if (line.startsWith('> ')) {
                // Handle blockquotes
                return `<blockquote style="border-left: 4px solid #e2e8f0; padding-left: 15px; margin: 10px 0; color: #718096; font-style: italic;">${escapeHtml(line.substring(2))}</blockquote>`
              } else if (line.startsWith('- ')) {
                return `<li style="margin-left: 20px; margin-bottom: 5px;">${escapeHtml(line.substring(2))}</li>`
              } else if (/^\d+\.\s/.test(line)) {
                // Handle numbered lists
                return `<li style="margin-left: 20px; margin-bottom: 5px; list-style-type: decimal;">${escapeHtml(line.substring(line.indexOf('.') + 2))}</li>`
              } else if (line.startsWith('**') && line.endsWith('**')) {
                // Handle bold text with double asterisks
                return `<p style="margin-bottom: 10px;"><strong>${escapeHtml(line.substring(2, line.length - 2))}</strong></p>`
              } else if (line.startsWith('*') && line.endsWith('*') && !line.includes(' ')) {
                // Handle bold text with single asterisk (for backwards compatibility)
                return `<p style="margin-bottom: 10px;"><strong>${escapeHtml(line.substring(1, line.length - 1))}</strong></p>`
              } else if (line.startsWith('*')) {
                // Handle italic text
                return `<p style="margin-bottom: 10px; font-style: italic;">${escapeHtml(line.substring(1))}</p>`
              } else if (line.startsWith('---')) {
                // Handle horizontal rule
                return '<hr style="margin: 20px 0; border: 0; border-top: 1px solid #e2e8f0;"/>'
              } else if (line.trim() === '') {
                return ''  // Skip empty lines instead of adding breaks
              } else {
                return `<p style="margin-bottom: 10px;">${escapeHtml(line)}</p>`
              }
            }).join('')}
          </div>
        </div>
      `

      const element = document.createElement('div')
      element.innerHTML = htmlContent
      document.body.appendChild(element) // Temporarily add to DOM
      
      const opt = {
        margin: 10,
        filename: `${documentTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_summary.pdf`,
        image: { type: 'jpeg' as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
        // Supported at runtime, missing from html2pdf.js's bundled typings
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      }

      try {
        // Loaded on demand (jspdf + html2canvas are large); see
        // src/@types/html2pdf-src.d.ts for why the sources are imported.
        const { default: html2pdf } = await import('html2pdf.js/src/index.js')
        await html2pdf().set(opt).from(element).save()
      } finally {
        document.body.removeChild(element) // Remove from DOM after generating PDF
      }
    } catch (error) {
      console.error('Failed to generate PDF:', error)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-primary-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="h-6 w-6 text-white mr-3" />
                <h3 className="text-lg font-medium text-white">
                  {title}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>
          
          {/* Content */}
          <div className="bg-white px-6 py-4">
            {isLoading ? (
              <div className="text-center py-12">
                <Loader2 className="animate-spin h-12 w-12 text-primary-500 mx-auto mb-4" />
                <p className="text-gray-600">{t('summaryModal.generating')}</p>
                <p className="text-sm text-gray-500 mt-2">{t('summaryModal.generatingHint')}</p>
              </div>
            ) : summary ? (
              <>
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-gray-200">
                  <button
                    onClick={handleCopyToClipboard}
                    className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isCopied 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isCopied ? (
                      <>
                        <CheckCircle size={16} className="mr-2" />
                        {t('summaryModal.copied')}
                      </>
                    ) : (
                      <>
                        <Copy size={16} className="mr-2" />
                        {t('summaryModal.copyToClipboard')}
                      </>
                    )}
                  </button>

                  <button
                    onClick={handleDownloadText}
                    className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                  >
                    <FileDown size={16} className="mr-2" />
                    {t('summaryModal.downloadText')}
                  </button>
                  
                  <button
                    onClick={handleDownloadPdf}
                    disabled={isGeneratingPdf}
                    className="flex items-center px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingPdf ? (
                      <>
                        <Loader2 size={16} className="mr-2 animate-spin" />
                        {t('summaryModal.generatingPdf')}
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2" />
                        {t('summaryModal.downloadPdf')}
                      </>
                    )}
                  </button>
                </div>
                
                {/* Summary content */}
                <div className="prose prose-sm max-w-none max-h-[60vh] overflow-y-auto">
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => <h1 className="text-2xl font-bold mt-6 mb-4 text-gray-900">{children}</h1>,
                      h2: ({ children }) => <h2 className="text-xl font-semibold mt-5 mb-3 text-gray-800">{children}</h2>,
                      h3: ({ children }) => <h3 className="text-lg font-medium mt-4 mb-2 text-gray-700">{children}</h3>,
                      p: ({ children }) => <p className="mb-3 text-gray-600 leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="text-gray-600">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      blockquote: ({ children }) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600">{children}</blockquote>,
                    }}
                  >
                    {summary}
                  </ReactMarkdown>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">{t('summaryModal.empty')}</p>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}